// Screen 17 · Files and attachments.
// Answers: where is the drawing, the site dimension or the photo this step needs?
// One upload control everywhere, with "attach to" pre-filled from where you are.
//
// On a job the tab is two parts on one screen since the Views pack (Spencer, 2026-10-23; docs/views-pack-brief.md; the drawing is
// docs/screens/views-pack-screens-round1.html, section 1): Views on top (the chosen drawings, one row each, in walk order: grip,
// name typed in place, kind, Primary, the sheet with who filed it and when, how many items are marked on it, Place marks, Replace
// sheet, Remove, and the revision that holds it), Uploads below (the inbox: everything filed to the job, folded to its count). An
// upload becomes a view from its row; its row then reads which view shows it. Nothing is deleted: a removed view or upload is
// retired, behind Show removed, with Restore. The other callers (an opportunity's files, the shop) keep FileList as it was.

import { useQuery } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { currentViews, isOverview, kindOf as viewKindOf, placement, type ViewKind } from '@zerogap/engine';
import { useCrumb } from '../../frame/Frame';
import { deleteRow, insertRow, toast, updateRow, useRows, type Row } from '../../lib/api';
import { longDate } from '../../lib/format';
import { jobTitle, useAllJobs } from '../../lib/jobs';
import { useSession } from '../../lib/session';
import { supabase } from '../../lib/supabase';
import { Btn, Edit, Empty, Fold, Head, Loading, Panel, Tag, useFolds } from '../../ui/kit';
import { Grip, nextSort, useDragRows } from '../../ui/move';
import { stamp, useFits } from '../steps/StepTable';
import { replaceSheet, useSvgBlank } from './walk/Drawing';
import { useWalkModel } from './walk/model';
import type { JobCtx } from './Workspace';
import '../../styles/jobview-files.css';

export function useSignedUrl(path: string | null | undefined): string | null {
  const q = useQuery({
    queryKey: ['signed-url', path],
    enabled: !!path,
    staleTime: 50 * 60_000,
    queryFn: async () => {
      const { data, error } = await supabase.storage.from('files').createSignedUrl(path!, 3600);
      if (error) throw new Error(error.message);
      return data.signedUrl;
    },
  });
  return q.data ?? null;
}

export async function uploadFile(file: File, attachedType: string, attachedId: string, userId: string | null, kind: string | null = null): Promise<Row> {
  const safe = file.name.replace(/[^A-Za-z0-9._-]+/g, '_');
  const path = `${attachedType}/${attachedId}/${Date.now()}_${safe}`;
  const { error } = await supabase.storage.from('files').upload(path, file, { contentType: file.type || undefined });
  if (error) {
    toast(`Could not upload: ${error.message}`);
    throw new Error(error.message);
  }
  return insertRow('file', {
    attached_type: attachedType, attached_id: attachedId, storage_path: path, filename: file.name,
    mime: file.type || null, size_bytes: file.size, uploaded_by: userId, kind,
  });
}

function Thumb({ file, small }: { file: Row; small?: boolean }) {
  const url = useSignedUrl(file.storage_path);
  const image = String(file.mime ?? '').startsWith('image/');
  const h = small ? 40 : 96;
  return (
    <a href={url ?? undefined} target="_blank" rel="noreferrer" className="pic" style={{ display: 'block', height: h, width: small ? 56 : undefined }}>
      {image && url ? <img src={url} alt={file.filename} style={{ height: '100%', width: small ? '100%' : undefined, objectFit: 'cover' }} /> : <div className="blank" style={{ aspectRatio: 'auto', height: '100%', padding: small ? 0 : undefined, fontSize: small ? 10 : undefined }}>{image ? '' : (file.filename.split('.').pop() ?? 'file').toUpperCase()}</div>}
    </a>
  );
}

export function FileList({ attachedType, attachedId, jobId, extra }: { attachedType: string; attachedId: string; jobId?: string; extra?: { type: string; ids: string[]; label: (id: string) => string }[] }) {
  const { user, can, setting } = useSession();
  const kinds = setting<ViewKind[]>('view_kinds', []);
  const fileKinds = setting<{ key: string; label: string }[]>('file_kinds', []);
  const own = useRows('file', { eq: { attached_type: attachedType, attached_id: attachedId }, order: ['-created_at'] });
  const views = useRows('view', { eq: { job_id: jobId }, enabled: !!jobId });
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  const pick = async (list: FileList | null) => {
    if (!list || list.length === 0) return;
    setBusy(true);
    try {
      for (const f of Array.from(list)) await uploadFile(f, attachedType, attachedId, user?.id ?? null);
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  };
  const useAsView = async (file: Row) => {
    if (!jobId) return;
    await insertRow('view', { job_id: jobId, file_id: file.id, name: file.filename.replace(/\.[^.]+$/, ''), kind: kinds.find((k) => /photo/i.test(k.key))?.key ?? kinds.at(-1)?.key ?? null, is_primary: views.rows.length === 0, sort: views.rows.length + 1 });
    toast('Added as a view. Set its kind on the View panel; it is ready for marks.');
  };

  return (
    <div>
      <div className="row mid" style={{ marginBottom: 8 }}>
        <input ref={input} type="file" multiple accept="image/*,application/pdf,.dwg,.dxf,.skp,.xlsx,.csv" className="hide" onChange={(e) => void pick(e.target.files)} />
        <Btn primary disabled={busy} onClick={() => input.current?.click()}>
          {busy ? 'Uploading…' : 'Upload'}
        </Btn>
        <small>Photos, drawings, PDFs. On a phone this opens the camera or the photo library.</small>
      </div>
      {own.loading ? (
        <Loading />
      ) : own.rows.filter((f) => !f.retired_at).length === 0 ? (
        <Empty>Nothing attached yet.</Empty>
      ) : (
        <div className="tiles">
          {own.rows.filter((f) => !f.retired_at).map((f) => {
            const isView = views.rows.some((v) => v.file_id === f.id);
            const replaced = isView && !currentViews(views.rows as any[]).some((v) => v.file_id === f.id);
            const image = String(f.mime ?? '').startsWith('image/');
            return (
              <div key={f.id} className="tile">
                <Thumb file={f} />
                <div style={{ marginTop: 6, wordBreak: 'break-word' }}>
                  {f.filename} {isView && <Tag>View</Tag>} {replaced && <Tag kind="off">replaced by a later sheet</Tag>}
                </div>
                <small>
                  {longDate(f.created_at)}
                  {f.note ? ` · ${f.note}` : ''}
                </small>
                {fileKinds.length > 0 && (
                  <div>
                    <select disabled={!can('screen.files')} value={f.kind ?? ''} title="What this file is" onChange={(e) => void updateRow('file', f.id, { kind: e.target.value || null })}>
                      <option value="">Kind not set</option>
                      {fileKinds.map((k) => (
                        <option key={k.key} value={k.key}>
                          {k.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="row mid" style={{ marginTop: 6, gap: 6 }}>
                  {jobId && image && !isView && (
                    <Btn small onClick={() => void useAsView(f)}>
                      Use as view
                    </Btn>
                  )}
                  {can('screen.jobs') && !isView && (
                    <Btn
                      small
                      onClick={async () => {
                        if (!window.confirm(`Remove ${f.filename}?`)) return;
                        await supabase.storage.from('files').remove([f.storage_path]);
                        await deleteRow('file', f.id);
                      }}
                    >
                      Remove
                    </Btn>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {extra?.map((x) => <Attached key={x.type} type={x.type} ids={x.ids} label={x.label} />)}
    </div>
  );
}

function Attached({ type, ids, label }: { type: string; ids: string[]; label: (id: string) => string }) {
  const files = useRows('file', { eq: { attached_type: type }, in: { attached_id: ids } });
  const live = files.rows.filter((f) => !f.retired_at);
  if (live.length === 0) return null;
  return (
    <div style={{ marginTop: 14 }}>
      <div className="lb">
        <span>Attached to {type === 'item' ? 'items' : 'cards'}</span>
      </div>
      <div className="tiles">
        {live.map((f) => (
          <div key={f.id} className="tile">
            <Thumb file={f} />
            <div style={{ marginTop: 6 }}>{f.filename}</div>
            <small>{label(f.attached_id)}</small>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- the job's Files tab: Views over Uploads (the Views pack, 2026-10-24) ----------

const stem = (name: unknown) => String(name ?? '').replace(/\.[^.]+$/, '').trim().toLowerCase();
const plainName = (name: unknown) => String(name ?? '').replace(/\(.*?\)/g, ' ').replace(/[_\-·]+/g, ' ').replace(/\s+/g, ' ').trim().toLowerCase();

/** A view that duplicates an earlier one: the same file, a file of the same name in another format (the five Maha PNG copies), or the same name. */
function duplicateOf(views: Row[], files: Row[], k: number): string | null {
  const v = views[k]!;
  const f = files.find((x) => x.id === v.file_id);
  for (let j = 0; j < k; j++) {
    const o = views[j]!;
    if (o.file_id && o.file_id === v.file_id) return `same file as View ${j + 1}`;
    const g = files.find((x) => x.id === o.file_id);
    if (f && g && stem(f.filename) && stem(f.filename) === stem(g.filename)) return `same drawing as View ${j + 1}`;
    if (plainName(v.name) && plainName(v.name) === plainName(o.name)) return `named like View ${j + 1}`;
  }
  return null;
}

export function JobViews({ ctx }: { ctx: JobCtx }) {
  const s = useSession();
  const { jobId, data } = ctx;
  const canEdit = s.canEdit('screen.item') && !ctx.locked;
  const kinds = s.setting<ViewKind[]>('view_kinds', []);
  const fileKinds = s.setting<{ key: string; label: string }[]>('file_kinds', []);
  // the uploads filed to this job, and the files its views show (a copied job's views show the original job's files)
  const uploads = useRows('file', { eq: { attached_type: 'job', attached_id: jobId }, order: ['-created_at'] });
  const viewFiles = useRows('file', { in: { id: data.allViews.map((v) => v.file_id as string).filter(Boolean) } });
  const files = { loading: uploads.loading, rows: [...uploads.rows, ...viewFiles.rows.filter((f) => !uploads.rows.some((u) => u.id === f.id))] };
  const users = useRows('user');
  const revisions = useRows('revision', { eq: { job_id: jobId } });
  const model = useWalkModel(ctx);
  const views = data.views;
  const removed = data.allViews.filter((v) => v.retired_at).sort((a, b) => Number(a.sort ?? 0) - Number(b.sort ?? 0));
  const placed = placement(model.res, views as any[], data.marks as any[]);
  const drag = useDragRows('view', views, !canEdit);
  const { ref, fits } = useFits<HTMLDivElement>(1060);
  const folds = useFolds('files');
  const [showRemoved, setShowRemoved] = useState(false);
  const [showRemovedFiles, setShowRemovedFiles] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const sheet = useRef<HTMLInputElement>(null);
  const replacing = useRef<Row | null>(null);
  const [busy, setBusy] = useState(false);
  const byUser = (id: unknown) => String(users.rows.find((u) => u.id === id)?.name ?? '').split(' ')[0] || '';
  const liveFiles = files.rows.filter((f) => !f.retired_at);
  const removedFiles = files.rows.filter((f) => f.retired_at);
  const viewOf = (fileId: unknown) => views.find((v) => v.file_id === fileId);
  const overviewKey = kinds.find((k) => k.overview)?.key ?? '';

  const pick = async (list: FileList | null) => {
    if (!list || list.length === 0) return;
    setBusy(true);
    try {
      for (const f of Array.from(list)) await uploadFile(f, 'job', jobId, s.user?.id ?? null);
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  };
  const useAsView = async (file: Row) => {
    await insertRow('view', { job_id: jobId, file_id: file.id, name: file.filename.replace(/\.[^.]+$/, ''), kind: kinds.find((k) => /line/i.test(k.key))?.key ?? kinds.find((k) => /photo/i.test(k.key))?.key ?? kinds.at(-1)?.key ?? null, is_primary: views.length === 0, sort: nextSort(data.allViews) });
    toast('Added as a view, at the bottom of the Views. Set its kind on its row; it is ready for marks.');
  };
  const setKind = async (v: Row, key: string) => {
    await updateRow('view', v.id, { kind: key || null, is_overview: !!key && kinds.find((k) => k.key === key)?.overview === true });
  };
  const makePrimary = async (v: Row) => {
    for (const x of views) if (x.is_primary && x.id !== v.id) await updateRow('view', x.id, { is_primary: false });
    await updateRow('view', v.id, { is_primary: true });
  };
  const onSheet = async (list: FileList | null) => {
    const picked = list?.[0];
    const v = replacing.current;
    replacing.current = null;
    if (sheet.current) sheet.current.value = '';
    if (!picked || !v) return;
    await replaceSheet(ctx, s.user?.id ?? null, v, picked);
  };
  const placeMarks = (v: Row) => ctx.go({ tab: 'item', view: v.id as string, item: null, place: '1', show: null, lit: null, back: null });
  const remove = async (v: Row) => {
    const name = String(v.name || 'this view');
    const rev = v.revision_id ? revisions.rows.find((x) => x.id === v.revision_id) : null;
    const n = data.marks.filter((m) => m.view_id === v.id).length;
    const ask = `Remove the view "${name}"?${n ? ` Its ${n} ${n === 1 ? 'mark goes' : 'marks go'} with it.` : ''}${rev ? ` It keeps its sheet in Rev ${rev.rev_no} and loses only its current use.` : ''} Nothing is deleted: Restore brings it back.`;
    if (!window.confirm(ask)) return;
    await updateRow('view', v.id, { retired_at: new Date().toISOString(), is_primary: false });
    if (v.is_primary) {
      const next = views.find((x) => x.id !== v.id);
      if (next) await updateRow('view', next.id, { is_primary: true });
    }
    const file = files.rows.find((f) => f.id === v.file_id);
    const shownElsewhere = views.some((x) => x.id !== v.id && x.file_id === v.file_id);
    if (file && !file.retired_at && !shownElsewhere && window.confirm(`Remove the upload ${file.filename} too? It stays in storage; Restore on the view brings both back.`)) {
      await updateRow('file', file.id, { retired_at: new Date().toISOString() });
    }
    toast(`${name} removed. It is under Show removed, with Restore.`);
  };
  const restore = async (v: Row) => {
    await updateRow('view', v.id, { retired_at: null });
    const file = files.rows.find((f) => f.id === v.file_id);
    if (file?.retired_at) await updateRow('file', file.id, { retired_at: null });
    toast(`${String(v.name || 'The view')} is back, with its marks.`);
  };
  const removeFile = async (f: Row) => {
    if (!window.confirm(`Remove ${f.filename}? It stays in storage, under Show removed, with Restore.`)) return;
    await updateRow('file', f.id, { retired_at: new Date().toISOString() });
  };

  const placedCount = placed.wanted.length - placed.unplaced.length;
  const revWords = (v: Row) => {
    const rev = v.revision_id ? revisions.rows.find((x) => x.id === v.revision_id) : null;
    return rev ? `holds its sheet · Rev ${rev.rev_no}` : 'not yet in a revision';
  };
  const kindValue = (v: Row) => (isOverview(v as any, kinds) && overviewKey ? overviewKey : viewKindOf(v as any, kinds)?.key ?? '');
  const kindPick = (v: Row) => (
    <select className="vkind" disabled={!canEdit} value={kindValue(v)} title="What kind of picture this is. A shop drawing is the kind the Engineering review checks the items against; an Overview is a top-down picture whose spots jump to the view of each wall." onChange={(e) => void setKind(v, e.target.value)}>
      <option value="">{v.kind && !viewKindOf(v as any, kinds) ? String(v.kind) : 'Kind not set'}</option>
      {kinds.map((k) => (
        <option key={k.key} value={k.key}>
          {k.label}
        </option>
      ))}
    </select>
  );
  const primaryCell = (v: Row) =>
    v.is_primary ? (
      <Tag kind="on" title="The primary view is the one the shop tablet shows">primary</Tag>
    ) : canEdit ? (
      <button type="button" className="linklike dim" title="The primary view is the one the shop tablet shows" onClick={() => void makePrimary(v)}>
        Make primary
      </button>
    ) : (
      <span className="only">—</span>
    );
  const actions = (v: Row) => (
    <span className="acts">
      {canEdit && (
        <>
          <Btn small onClick={() => placeMarks(v)} title="Open this sheet on Items built with placing on">
            Place marks
          </Btn>
          <Btn
            small
            title="Upload the new version of this sheet for the next revision. The old sheet stays with its own revision; its marks carry over by item number."
            onClick={() => {
              replacing.current = v;
              sheet.current?.click();
            }}
          >
            Replace sheet
          </Btn>
          <Btn small onClick={() => void remove(v)} title="Take this view and its marks off the job; nothing is deleted">
            Remove
          </Btn>
        </>
      )}
    </span>
  );

  return (
    <div className="vfiles" ref={ref}>
      <input ref={sheet} type="file" accept="image/*" className="hide" onChange={(e) => void onSheet(e.target.files)} />
      <Panel className="vpanel">
        <div className="sbar">
          <h4>Views</h4>
          <span className="only">the drawings this job is checked against, in walk order</span>
          <span className="sp" />
          <span className="only vcount" title="Top-level items with a mark on some View, of those that want one; a Whole-room item by design (a vendor order) is not counted">
            {placedCount} of {placed.wanted.length} items placed
          </span>
        </div>
        {views.length === 0 ? (
          <Empty>No view yet. Upload a drawing or a photo under Uploads and choose "Use as view".</Empty>
        ) : fits ? (
          <table className="vt">
            <thead>
              <tr>
                <th aria-label="Order" />
                <th>#</th>
                <th>Name</th>
                <th>Kind</th>
                <th>Primary</th>
                <th>Sheet</th>
                <th>Marked</th>
                <th aria-label="Actions" />
                <th>Revision</th>
              </tr>
            </thead>
            <tbody>
              {views.map((v, k) => (
                <tr key={v.id} {...drag.row(v.id as string)}>
                  <td className="g">{canEdit && <Grip {...drag.grip(v.id as string)} />}</td>
                  <td className="mono">{k + 1}</td>
                  <td className="nm">
                    <Edit className="vname" value={v.name} disabled={!canEdit} placeholder="Name" onSave={(t) => updateRow('view', v.id, { name: t.trim() || v.name })} />
                  </td>
                  <td>{kindPick(v)}</td>
                  <td>{primaryCell(v)}</td>
                  <td className="sheet">
                    <SheetCell view={v} views={views} files={files.rows} k={k} by={byUser} />
                  </td>
                  <td className="mono ct">
                    {placed.on_view[v.id as string] ?? 0} of {placed.wanted.length}
                  </td>
                  <td>{actions(v)}</td>
                  <td className="mut">{revWords(v)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="vrows">
            {views.map((v, k) => (
              <div key={v.id} className="vrow dragrow" {...drag.row(v.id as string, 'vrow dragrow')}>
                <span className="g">{canEdit && <Grip {...drag.grip(v.id as string)} />}</span>
                <span className="n mono">{k + 1}</span>
                <span className="nm">
                  <Edit className="vname" value={v.name} disabled={!canEdit} placeholder="Name" onSave={(t) => updateRow('view', v.id, { name: t.trim() || v.name })} />
                </span>
                <span className="mono ct">
                  {placed.on_view[v.id as string] ?? 0} of {placed.wanted.length}
                </span>
                {actions(v)}
                <span className="vals">
                  <span className="nv">
                    <span className="k">Kind</span>
                    {kindPick(v)}
                  </span>
                  <span className="nv">
                    <span className="k">Primary</span>
                    {primaryCell(v)}
                  </span>
                  <span className="nv wide">
                    <span className="k">Sheet</span>
                    <SheetCell view={v} views={views} files={files.rows} k={k} by={byUser} />
                  </span>
                  <span className="nv">
                    <span className="k">Revision</span>
                    <span className="mut">{revWords(v)}</span>
                  </span>
                </span>
              </div>
            ))}
          </div>
        )}
        {removed.length > 0 && (
          <div className="vremoved">
            <button type="button" className="lgrp fold" aria-expanded={showRemoved} onClick={() => setShowRemoved(!showRemoved)}>
              <span className={`chev${showRemoved ? ' open' : ''}`} /> Show removed · {removed.length}
            </button>
            {showRemoved &&
              removed.map((v) => {
                const f = files.rows.find((x) => x.id === v.file_id);
                const n = data.marks.filter((m) => m.view_id === v.id).length;
                return (
                  <div key={v.id} className="rrow">
                    <span className="nm">{String(v.name || 'View')}</span>
                    <span className="mut">
                      {f ? f.filename : 'no sheet'} · removed {String(v.retired_at).slice(0, 10)}
                      {n ? ` · ${n} ${n === 1 ? 'mark' : 'marks'} with it` : ''}
                      {f?.retired_at ? ' · its upload removed too' : ''}
                    </span>
                    <span className="sp" />
                    {canEdit && (
                      <Btn small onClick={() => void restore(v)}>
                        Restore
                      </Btn>
                    )}
                  </div>
                );
              })}
          </div>
        )}
      </Panel>

      <Fold
        className="upanel"
        open={folds.isOpen('uploads')}
        onToggle={() => folds.toggle('uploads')}
        label={<>Uploads · {liveFiles.length}</>}
        right={
          <span className="row mid" style={{ gap: 8 }}>
            <input ref={input} type="file" multiple accept="image/*,application/pdf,.dwg,.dxf,.skp,.xlsx,.csv" className="hide" onChange={(e) => void pick(e.target.files)} />
            <Btn primary small disabled={busy} onClick={() => input.current?.click()}>
              {busy ? 'Uploading…' : 'Upload'}
            </Btn>
          </span>
        }
      >
        <small className="uhint">Photos, drawings, PDFs. On a phone this opens the camera or the photo library. Not every upload is a view: "Use as view" makes one.</small>
        {files.loading ? (
          <Loading />
        ) : liveFiles.length === 0 ? (
          <Empty>Nothing uploaded yet.</Empty>
        ) : (
          <table className="ut">
            <thead>
              <tr>
                <th aria-label="Picture" />
                <th>File</th>
                <th>Filed</th>
                <th>Used as</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {liveFiles.map((f) => {
                const v = viewOf(f.id);
                const anyView = data.allViews.find((x) => x.file_id === f.id);
                const image = String(f.mime ?? '').startsWith('image/');
                return (
                  <tr key={f.id}>
                    <td className="th">
                      <Thumb file={f} small />
                    </td>
                    <td className="fn">
                      <span className="mono">{f.filename}</span>
                      {f.note && <span className="under">{f.note}</span>}
                      {fileKinds.length > 0 && (
                        <select className="fkind" disabled={!s.can('screen.files')} value={f.kind ?? ''} title="What this file is" onChange={(e) => void updateRow('file', f.id, { kind: e.target.value || null })}>
                          <option value="">Kind not set</option>
                          {fileKinds.map((k) => (
                            <option key={k.key} value={k.key}>
                              {k.label}
                            </option>
                          ))}
                        </select>
                      )}
                    </td>
                    <td className="mut">
                      {byUser(f.uploaded_by)}
                      {byUser(f.uploaded_by) ? ' · ' : ''}
                      {stamp(f.created_at)}
                    </td>
                    <td>
                      {v ? (
                        <Tag kind="on" title="This upload is a view: its row is above">
                          View {views.indexOf(v) + 1} · {String(v.name || '')}
                        </Tag>
                      ) : anyView && !anyView.retired_at ? (
                        <Tag kind="off">replaced by a later sheet</Tag>
                      ) : anyView?.retired_at ? (
                        <span className="only">its view was removed · Restore under Views</span>
                      ) : image && canEdit ? (
                        <Btn small onClick={() => void useAsView(f)}>
                          Use as view
                        </Btn>
                      ) : (
                        <span className="only">{image ? '—' : 'a file, not a view'}</span>
                      )}
                    </td>
                    <td className="acts">
                      {v ? <span className="only">Remove is on the view</span> : s.can('screen.jobs') && !anyView && <Btn small onClick={() => void removeFile(f)}>Remove</Btn>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {removedFiles.length > 0 && (
          <div className="vremoved">
            <button type="button" className="lgrp fold" aria-expanded={showRemovedFiles} onClick={() => setShowRemovedFiles(!showRemovedFiles)}>
              <span className={`chev${showRemovedFiles ? ' open' : ''}`} /> Show removed uploads · {removedFiles.length}
            </button>
            {showRemovedFiles &&
              removedFiles.map((f) => (
                <div key={f.id} className="rrow">
                  <span className="mono">{f.filename}</span>
                  <span className="mut">removed {String(f.retired_at).slice(0, 10)}</span>
                  <span className="sp" />
                  {s.can('screen.jobs') && (
                    <Btn small onClick={() => void updateRow('file', f.id, { retired_at: null })}>
                      Restore
                    </Btn>
                  )}
                </div>
              ))}
          </div>
        )}
        <Attached type="item" ids={data.items.map((i) => i.id as string)} label={(id) => String(data.items.find((i) => i.id === id)?.number ?? 'item')} />
      </Fold>
    </div>
  );
}

/** The sheet a view shows: its file name, who filed it and when, "shows blank" on an SVG with no size, and which earlier view it duplicates. */
function SheetCell({ view, views, files, k, by }: { view: Row; views: Row[]; files: Row[]; k: number; by: (id: unknown) => string }) {
  const f = files.find((x) => x.id === view.file_id);
  const url = useSignedUrl(f?.storage_path);
  const blank = useSvgBlank(url, /svg/i.test(String(f?.mime ?? f?.filename ?? '')));
  const dup = duplicateOf(views, files, k);
  if (!f) return <span className="only">no sheet</span>;
  return (
    <>
      <span className="mono fn">{f.filename}</span>
      <span className="under">
        {by(f.uploaded_by)}
        {by(f.uploaded_by) ? ' · ' : ''}
        {stamp(f.created_at)}
        {blank && (
          <>
            {' · '}
            <Tag kind="un" title="The SVG carries no width, height or viewBox, so it cannot be drawn">shows blank</Tag>
          </>
        )}
        {dup && (
          <>
            {' · '}
            <Tag kind="un" title="Another view already shows this drawing or carries this name; Remove takes this one off the job, Restore brings it back">{dup}</Tag>
          </>
        )}
      </span>
    </>
  );
}

/** The Files destination for people who see files but not the job workspace (the shop). */
export function AllFiles() {
  useCrumb([{ label: 'Files' }]);
  const all = useAllJobs();
  const files = useRows('file', { order: ['-created_at'] });
  if (all.loading || files.loading) return <Loading />;
  const live = files.rows.filter((f) => !f.retired_at);
  const jobs = all.jobs.filter((j) => live.some((f) => f.attached_type === 'job' && f.attached_id === j.job.id));
  return (
    <div className="stack">
      <Head title="Files" sub="Every job's drawings, dimensions and photos." />
      {jobs.length === 0 && <Empty>No files yet.</Empty>}
      {jobs.map((j) => (
        <Panel key={j.job.id} label={jobTitle(j)}>
          <div className="tiles">
            {live
              .filter((f) => f.attached_type === 'job' && f.attached_id === j.job.id)
              .map((f) => (
                <div key={f.id} className="tile">
                  <Thumb file={f} />
                  <div style={{ marginTop: 6 }}>{f.filename}</div>
                  <small>{longDate(f.created_at)}</small>
                </div>
              ))}
          </div>
        </Panel>
      ))}
      <small>
        Looking for a card's files? Open the card on the <Link to="/shop">board</Link>.
      </small>
    </div>
  );
}
